/* eslint-disable no-console */
import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Form, Icon, Input, Modal, message } from 'antd';
import { subscribe } from 'alfa';

import JWK from '../../helpers/jwk';
import UserRepo from '../../repository/user';
import Security from '../../helpers/security';

const KeyCreator = ({ user, form }) => {
  const [visible, setVisible] = useState(false);
  const [confirmDirty, setConfirmDirty] = useState(false);
  const { getFieldDecorator } = form;

  const open = useCallback(() => {
    setVisible(true);
  }, []);

  const close = useCallback(() => {
    setVisible(false);
  }, []);

  const downloadFile = (fileContent) => {
    const fileName = 'b2b.key';
    const blob = new Blob([fileContent], { type: 'text/plain' });
    const anchorNode = document.createElement('a');
    anchorNode.setAttribute('download', fileName);
    anchorNode.setAttribute('href', window.URL.createObjectURL(blob));
    anchorNode.click();
  };

  const encryptAndStore = async (key, password) => {
    try {
      const result = await JWK.encrypt(key, password);
      const encryptedKey = JSON.stringify(result);
      Security.registerKey(user.getData(), encryptedKey);
      downloadFile(encryptedKey);
      await user.ping(); // refresh keys
      close();
      Modal.success({
        title: 'Password created successfully',
        content: (
          <p>
            Make sure to store the downloaded <strong>b2b.key</strong> file in a safe and private place.
          </p>
        ),
      });
    } catch (error) {
      console.error('ERROR: KeyCreator: JWK.decrypt()', error);
      message.error('Error encrypting your key.');
    }
  };

  const handleSubmit = () => {
    form.validateFieldsAndScroll(async (err, values) => {
      if (err) { return; }
      try {
        const jwk = JWK.generate();
        await UserRepo.addKey(jwk.publicKey);
        encryptAndStore(jwk.privateKey, values.password);
      } catch (error) {
        console.error(error.response);
        message.error('Error storing you key. Try again.');
      }
    });
  };

  const handleConfirmBlur = (event) => {
    const { value } = event.target;
    setConfirmDirty(confirmDirty || !!value);
  };

  const validateToNextPassword = (rule, value, callback) => {
    if (value && confirmDirty) {
      form.validateFields(['confirm'], { force: true });
    }
    callback();
  };

  const compareToFirstPassword = (rule, value, callback) => {
    if (value && value !== form.getFieldValue('password')) {
      callback('Two passwords that you enter is inconsistent!');
    }
    callback();
  };

  useEffect(() => {
    const shouldOpen = () => {
      const userData = user.getData();
      if (!userData) {
        return false;
      }
      const keys = (userData.public_keys || []).filter((key) => !key.disabled_at || key.disabled_at === '');
      return keys.length < 1;
    };

    if (shouldOpen()) {
      open();
    }
  }, [open, user]);

  return (
    <Modal
      title="Create B2B key"
      visible={visible}
      closable={false}
      width={500}
      footer={[
        <Button key="create" type="primary" onClick={handleSubmit}>
          <Icon type="safety-certificate" /> Create key
        </Button>,
      ]}
    >

      <p>Hi there!<br /><br /> We&apos;re trying to make <b>Lana</b> more secure every day.</p>
      <p>
        Now, to make transactions using <b>Lana B2B</b> you will need to have a <b>key</b>
        in order to sign your requests.<br />
        But don&apos;t worry, it seems more complicated than it really is.<br />
      </p>
      <p>
        You just need to create a <b>password</b> that will protected your <b>key</b>. This <b>key</b> will
        be downloaded and you&apos;ll need to inform this key if you change your machine or log out from your account.
      </p>

      <Form layout="vertical">
        <Form.Item label="Password" hasFeedback>
          {getFieldDecorator('password', {
            rules: [
              {
                required: true,
                message: 'Please input your password!',
              },
              {
                validator: validateToNextPassword,
              },
            ],
          })(<Input.Password prefix={<Icon type="key" />} />)}
        </Form.Item>
        <Form.Item label="Confirm Password" hasFeedback>
          {getFieldDecorator('confirm', {
            rules: [
              {
                required: true,
                message: 'Please confirm your password!',
              },
              {
                validator: compareToFirstPassword,
              },
            ],
          })(<Input.Password prefix={<Icon type="key" />} onBlur={handleConfirmBlur} />)}
        </Form.Item>
      </Form>
    </Modal>
  );
};

KeyCreator.propTypes = {
  form: PropTypes.shape({
    getFieldValue: PropTypes.func,
    validateFields: PropTypes.func,
    validateFieldsAndScroll: PropTypes.func,
    getFieldDecorator: PropTypes.func,
  }),
  user: PropTypes.shape({
    getData: PropTypes.func,
    ping: PropTypes.func,
  }),
};

export default subscribe(Form.create({ name: 'form-key-creator' })(KeyCreator), ['user']);
